// Self-hosted fonts (CSP requires font-src 'self'; fontsource bundles woff2 locally via Vite).
import '@fontsource/manrope/400.css';
import '@fontsource/manrope/500.css';
import '@fontsource/manrope/600.css';
import '@fontsource/manrope/700.css';
import '@fontsource/manrope/800.css';
import '@fontsource/space-grotesk/500.css';
import '@fontsource/space-grotesk/600.css';
import '@fontsource/space-grotesk/700.css';
import '@fontsource/pacifico/400.css';
import '@fontsource/jetbrains-mono/500.css';
import '@fontsource/jetbrains-mono/700.css';
import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { createHead, UnheadProvider } from '@unhead/react/client';
import './index.css';
import './i18n';
import App from './App.tsx';
import { requestPersistentStorage } from './storage/persistentStorage';
import { track } from './analytics/posthog';

const head = createHead();

// Backwards-compat: users with bookmarks from the HashRouter era arrive at
// `/#/library`. Rewrite to `/library` once before React mounts so they land
// on the right place under BrowserRouter.
if (window.location.hash.startsWith('#/')) {
  const target = window.location.hash.slice(1);
  window.history.replaceState(null, '', target);
}

// GCS website mode 301-redirects `/library` → `/library/index.html` (and same
// for any prerendered path). React Router has no route matching `*/index.html`,
// so the catch-all sends users to `/`. Strip the suffix before React mounts.
if (window.location.pathname.endsWith('/index.html')) {
  const cleaned = window.location.pathname.slice(0, -'index.html'.length);
  window.history.replaceState(
    null,
    '',
    cleaned + window.location.search + window.location.hash,
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <UnheadProvider head={head}>
      <BrowserRouter>
        <Suspense fallback={null}>
          <App />
        </Suspense>
      </BrowserRouter>
    </UnheadProvider>
  </StrictMode>,
);

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
}

// Ask the browser to make IndexedDB persistent so the user's local-first data
// isn't silently evicted under storage pressure. Best-effort and non-blocking.
// The result tells us how much of the user base has durable storage; track()
// buffers until analytics boots and consent is granted.
void requestPersistentStorage().then((result) => {
  track('persistent_storage', { result });
});
